import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import PropTypes from 'prop-types';
import { getBase64, getObjectValue } from "../utils/utils";
import DropZone from "../components/DropZone";
import SideNavbar from "../components/SideNavbar";
import Error from "./Error";
import Loading from "./Loading";
import AnimatedEllipsis from "../components/AnimatedEllipsis";

const loadingGIF = 'https://vale-prod-images.s3.us-east-2.amazonaws.com/deer-running.gif';
const dropZoneText = 'Drag and Drop trailcam images to upload for analysis.';

function ImageUpload({ currentProfile, setCurrentProfile, isMobile }) {
    const [uploadingImages, setUploadingImages] = useState(false);
    const [imagesObject, setImagesObject] = useState({});
    const [additionalImagesObject, setAdditionalImagesObject] = useState({});
    const [duplicatesCount, setDuplicatesCount] = useState(0);
    const [selectedSite, setSelectedSite] = useState(null);

    const { site } = useParams();

    let formattedSite = "site";
    if(site) {
        formattedSite = site.replaceAll("-wildlifeinvestments", "");
        let sites = formattedSite.split("-");
        formattedSite = sites.map((word) => {
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        }).join(' ');
    }

    useEffect(() => {
        document.title = formattedSite;
    });

    useEffect(() => {
        if(currentProfile && currentProfile.sites) {
            currentProfile.sites.forEach((s) => {
                if(s.slug === site) {
                    setSelectedSite(s);
                }
            });
        }
    }, [currentProfile, site]);

    const handleImageUpload = async (e, type) => {
        setDuplicatesCount(0);
        if(type === "delete") {
            if(getObjectValue(imagesObject, e)) {
                delete imagesObject[e];
            }
            setImagesObject({ ...imagesObject });
            if(getObjectValue(additionalImagesObject, e)) {
                delete additionalImagesObject[e];
            }
            setAdditionalImagesObject({ ...additionalImagesObject });
        } else {
            let newImagesObject = { ...additionalImagesObject };
            let duplicatesFound = 0;
            await Promise.all(e.map(async(image, index) => {
                let base64Image = await getBase64(image);
                if(!getObjectValue(imagesObject, base64Image)) {
                    newImagesObject[base64Image] = { 'file': e[index], 'error': false, 'success': false };
                } else {
                    duplicatesFound++;
                }
                return(true);
            }));
            if(duplicatesFound) {
                setDuplicatesCount(duplicatesFound);
            }
            setAdditionalImagesObject(newImagesObject);
            setImagesObject({...imagesObject, ...newImagesObject});
        }
    };

    if(selectedSite && currentProfile) {
        let sitesURI = "/sites";
        let adminLinks = [];
        if(currentProfile.isAdmin) {
            // sitesURI = `/${sub}/sites`;
            adminLinks = [{ "display": "Label Images", "url": `${sitesURI}/${site}/label`}];
        }
        let sideNavbarFilters = [{ "display": "Buck Inventory", "url": `${sitesURI}/${site}/inventory` },
                                 { "display": "Image Upload", "url": `${sitesURI}/${site}/upload` }, ...adminLinks];
        return (
            <>
                <SideNavbar isMobile={isMobile} propFilters={sideNavbarFilters} title={formattedSite} titleUrl={sitesURI} initialShow={true} />
                <div style={isMobile ? { "minHeight":"92vh" } : { "minHeight":"92vh", "maxWidth": "90%", "display": "flex", "justifyContent": "center" }} className="w3-content w3-container w3-padding-64" id="about">
                    <div>
                        <div style={isMobile ? { "width": "100%"} : { "width": "707px"}}>
                            <div style={isMobile ? {} : { "display": "flex", "marginTop": "16px" }}>
                                <h3 style={{ "width": "100%", "margin": "0px" }}>Upload Trailcam Photos</h3>
                            </div>
                            {duplicatesCount ? (
                                        <div style={{ "color": "red", "marginTop": "16px"}}>Duplicate Photos Found (Not Added): {duplicatesCount}</div>
                                    ) : null}
                            {uploadingImages ? (
                                <div style={{ "marginTop": "16px", "textAlign": "center" }}>
                                    <img style={{ "width": "50%" }} src={loadingGIF} alt="Elk Running" />
                                    <p style={{ "marginBottom": "0px" }}>Uploading Trailcam Images<AnimatedEllipsis/></p>
                                </div>
                            ) : (
                                <React.Fragment>
                                    <p>Select the trail cameras photos that you would like the Wildlife Investments Team to analyze.</p>
                                    <p><b>ONLY 500 IMAGES MAY BE UPLOADED AT A TIME, SELECTING MORE THAN THAT WILL RESULT IN NOTHING BEING SELECTED</b></p>
                                    <p><b>PLEASE MAKE SURE ALL IMAGES RELATED TO THIS SITE HAVE UNIQUE NAMES, OTHERWISE IMAGES WILL BE OVERWRITTEN!</b></p>
                                    <div style={{ "marginTop": "16px" }}>
                                        <DropZone acceptedFileTypes={{ 'image/*': ['.jpg', '.jpeg'] }} updateParent={handleImageUpload} imagesObject={imagesObject} submitted={uploadingImages} display={true} text={dropZoneText} maxFiles={500} isMobile={isMobile} />
                                    </div>
                                </React.Fragment>
                            )}
                        </div>
                    </div>
                </div>
                {Object.keys(additionalImagesObject).length === 0 || uploadingImages ? null : (
                    <div className="navbar-bottom">
                        <div className="w3-bar w3-wide w3-padding w3-card w3-white" style={{ "display": "flex", "justifyContent": "center" }}>
                            <div style={isMobile ? { "width": "100%", "display": "flex", "justifyContent": "right" } : { "width": "75%", "display": "flex", "justifyContent": "right" }}>
                                <button 
                                    style={isMobile ? { "width": "100%" } : { "width": "275px" }} 
                                    className="main-button"
                                    disabled={Object.keys(additionalImagesObject).length === 0 || uploadingImages} 
                                    onClick={async () => { 
                                        setUploadingImages(true);
                                        let updatedAdditionalImagesObject = await selectedSite.uploadImages(currentProfile.accessToken, additionalImagesObject);
                                        setImagesObject({...imagesObject, ...updatedAdditionalImagesObject });
                                        setUploadingImages(false);
                                    }}
                                    title={"Upload Images"}
                                >
                                    Upload Images
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </>
        );
    } else if(!currentProfile && typeof currentProfile === "boolean") {
        return(
            <Error isMobile={isMobile} type={"login"} />
        );
    } else {
        return(
            <Loading isMobile={isMobile} text={`${formattedSite}`} />
        );
    }
}

ImageUpload.propTypes = {
    isMobile: PropTypes.bool,
    currentProfile: PropTypes.object
};

export default ImageUpload;